/**
 * Credits (Phase 8): every third-party asset the game ships, from the pipeline's
 * `credits.json`, which is generated from the license register and checked against it in CI.
 * CC-BY work shows its exact credit line; CC0 sources are listed by author.
 */
import { CreditsSchema, type CreditEntry } from '@steelwake/shared/data';
import { colors, spacing } from '@steelwake/ui';
import { useEffect, useState } from 'react';

import { assetUrl } from '../game/assets/paths.js';
import { Card } from '../ui.js';

type State = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; entries: CreditEntry[] };

function groupLabel(license: string): string {
  if (license.startsWith('CC0')) return 'Public domain (CC0)';
  if (license.startsWith('CC-BY')) return 'Attribution (CC-BY)';
  return license;
}

export function Credits() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(assetUrl('credits.json'));
        if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
        const credits = CreditsSchema.parse((await res.json()) as unknown);
        if (!cancelled) setState({ kind: 'ready', entries: credits.entries });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = new Map<string, CreditEntry[]>();
  if (state.kind === 'ready') {
    for (const e of state.entries) {
      const list = groups.get(groupLabel(e.license)) ?? [];
      list.push(e);
      groups.set(groupLabel(e.license), list);
    }
  }

  return (
    <Card>
      <h1 style={{ marginTop: 0 }}>Credits</h1>
      <p style={{ color: colors.textMuted, fontSize: 14 }}>
        The mechs, arenas, effects and interface are original work for STEELWAKE. The bodies,
        textures, skies, sounds and music below come from the people and projects listed, under the
        licenses shown; thank you.
      </p>
      {state.kind === 'loading' ? <p data-testid="credits-loading">Loading…</p> : null}
      {state.kind === 'error' ? (
        <p role="alert" data-testid="credits-error" style={{ color: colors.danger }}>
          The credits could not be loaded.
        </p>
      ) : null}
      {[...groups.entries()].map(([label, entries]) => (
        <section key={label} data-testid="credits-group" style={{ marginTop: spacing.lg }}>
          <h2 style={{ fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {label}
          </h2>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6, fontSize: 14 }}>
            {entries.map((e) => (
              <li key={e.file} data-testid="credits-entry">
                {e.attribution ? (
                  <span>{e.attribution}</span>
                ) : (
                  <span>
                    <b>{e.author}</b>
                    <span style={{ color: colors.textMuted }}> · {e.license}</span>
                  </span>
                )}{' '}
                <a
                  href={e.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: colors.accent }}
                >
                  source
                </a>
                <span style={{ color: colors.textMuted, fontSize: 12 }}>
                  {' '}
                  · <code>{e.file}</code>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </Card>
  );
}
